
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import DoneRoundedIcon from "@mui/icons-material/DoneRounded";
import { Box, Typography, Button } from "@mui/material";
import KeyboardTabOutlinedIcon from "@mui/icons-material/KeyboardTabOutlined";
import axiosInstance from "../../../axiosInstance";
import { RootState } from "../../../redux/store";
import { housingEditorTourSteps } from "../housingEditorTour";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import { setGraphData, setSelectedFloorPlan } from '../../../redux/features/graph';
import { GuidedTour } from "./GuidedTour";
import { inputGraphEditorTourSteps } from "../inputGraphEditorTour";

interface TopNavProps {
  sidebarOpen: boolean;
  setSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  LeftsidebarOpen: boolean;
  setLeftSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function TopNav({
  sidebarOpen,
  setSidebarOpen,
  LeftsidebarOpen,
  setLeftSidebarOpen,
}: TopNavProps) {
  const [isRenameMode, setRenameMode] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [documentTitle, setDocumentTitle] = useState("Untitled Document 1");
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const visualViewport = window.visualViewport;

  const location = useLocation();
  const dispatch = useDispatch();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleDocumentTitleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setDocumentTitle(event.target.value);
  };

  const { state } = useLocation();


  useEffect(() => {
    if (state?.documentTitle) {
      setDocumentTitle(state.documentTitle);
    }
  }, [state]);
  const handleTitleDoubleClick = () => {
    setRenameMode(true);
  };

  const handleTitleKeyPress = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "Enter" && documentTitle !== "") {
      setRenameMode(false);
    }
  };

  const handleTitleComplete = () => {
    if (documentTitle !== "") {
      setRenameMode(false);
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };
  const toggleLeftSidebar = () => {
    setLeftSidebarOpen(!LeftsidebarOpen);
  };

  const navigateToHome = () => {
    navigate("/home");
  };

  const selectedFloorPlan = useSelector(
    (state: RootState) => state.graph.selectedFloorPlan,
  );
  const graphData = useSelector((state: RootState) => state.graph.graphData);

  const currentNodes = useSelector((state: RootState) => state.nodesState.nodes);
  const currentLinks = useSelector((state: RootState) => state.nodesState.links);
  const plotWidth = useSelector((state: RootState) => state.nodesState.plotWidth);
  const plotHeight = useSelector((state: RootState) => state.nodesState.plotHeight);
  const selectedTemplate = useSelector(
  (state: RootState) => state.templateState.selectedTemplate
);

  const postData = async () => {
    setIsSaving(true);
    console.log("Save graph initiated");

    // Process nodes with normalized coordinates
    let nodesNew: any[] = [];
    currentNodes.forEach((ele) => {
      const height = visualViewport?.height || window.innerHeight;
      const width = visualViewport?.width || window.innerWidth;
      if (width && height)
        nodesNew.push({
          ...ele,
          x: ele.x / width,
          y: ele.y / height,
          id: ele.id // IMPORTANT: Preserve exact ID
        });
    });

    // Process edges to ensure complete properties
    const edgesNew = currentLinks.map(link => ({
      source: link.source,
      target: link.target,
      color: link.color || 'black'
    }));

    // Create updated graph data with normalized nodes and processed edges
    const updatedGraphData = {
      ...graphData,
      nodes: nodesNew,
      edges: edgesNew,
        plot_width: plotWidth || 0,  
        plot_height: plotHeight || 0  
    };

    // Update Redux state
    dispatch(setGraphData(updatedGraphData));

    console.log("Processed nodes:", nodesNew.length);
    console.log("Processed edges:", edgesNew.length);


    const data = {
      name: `${documentTitle} [GRAPH]`,
      floorplans: selectedFloorPlan,
      graph_saved_data: {
        ...graphData,
        nodes: nodesNew,
        edges: edgesNew,
        plot_width: plotWidth || 0, // Use the entered plot width
        plot_height: plotHeight || 0, // Use the entered plot height
      },
    };

    console.log("Prepared data for saving:", data); // Log data being sent


    const url = `${process.env.REACT_APP_BASEURL}/api/document/`;
    const token = localStorage.getItem("access_token");
    console.log("TOKEN: ", token);
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };



    try {
      console.log("Sending request to:", url);
      const response = await axiosInstance.post(url, data, { headers });
      console.log("Graph saved successfully:", response.data);


      // Save to local storage
      const fileKey = `${documentTitle} [GRAPH]`;
      let savedGraphData = JSON.parse(localStorage.getItem("graphDataMap") || "{}");
      savedGraphData[fileKey] = {
        plotWidth: plotWidth,
        plotHeight: plotHeight,
        edges: edgesNew  // Use the PROCESSED edges, not graphData.edges
      };

      localStorage.setItem("graphDataMap", JSON.stringify(savedGraphData));
      console.log("Graph data saved to localStorage with", edgesNew.length, "edges");

    } catch (error) {
      console.error("Error saving graph:", error);
      // Show error to user if needed
    } finally {
      // Ensure the dialog closes and saving state is reset regardless of success/failure
      setIsOpen(false);
      setIsSaving(false);
      console.log("Save process completed, dialog closed");
    }
  };




  return (
    <div
      style={{
        position: "fixed",
        zIndex: 1000,
        left: "14.5px",
        top: "18.5px",
        width: "73%",
        transition: "all .25s linear",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            backgroundColor: "#FF9A56",
            borderRadius: "10px",
            border: "2px solid #1a1a1a",
            padding: "8px 17px",
            cursor: "pointer",
            transition: "all 0.1s ease",
            "&:hover": {
              transform: "translate(-1px, -1px)",
            },
            "&:active": {
              transform: "translate(1px, 1px)",
            },
          }}
          onClick={navigateToHome}
        >

          <ArrowBackIcon
            htmlColor="#FBE5C6"
            sx={{
              padding: "3px",
            }}
          />
          <Typography
            sx={{
              pl: "5px",
              color: "#FBE5C6",
              textAlign: "left",
              fontFamily: "Poppins",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: 500,
              lineHeight: "24px",
            }}
          >
            Back
          </Typography>
        </Box>


      </Box>




      <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
        <DialogTitle>Enter Document Title</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Document Title"
            fullWidth
            variant="outlined"
            value={documentTitle}
            onChange={(e) => setDocumentTitle(e.target.value)}
          />
        </DialogContent>
        <DialogActions>

          <Button onClick={() => setIsOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={postData}
            disabled={isSaving}
            color="primary"
            variant="contained"
            className="save-graph-btn"
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>

      </Dialog>
      <Box sx={{ display: "flex", gap: "1rem" }}>
        {/*<Box sx={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#FBE5C6',
            borderRadius: '8px',
            padding: '12px',
          }}>
            <Typography variant="h6" style={{ flexGrow: 1, textAlign: 'center', color: 'black', fontFamily: 'Montserrat', fontSize: 16, fontWeight: 600 }} onDoubleClick={handleTitleDoubleClick}>
              {isRenameMode ? (
                <>
                  <input type="text" value={documentTitle} onChange={handleDocumentTitleChange} onKeyPress={handleTitleKeyPress} onBlur={handleTitleComplete} style={{ border: 'none', outline: 'none', minWidth: '150px', borderBottom: '2px solid black', color: 'black', fontFamily: 'Montserrat', fontSize: 16, fontWeight: 600 }} />
                  <DoneRoundedIcon sx={{ marginLeft: '8px', verticalAlign: 'middle', cursor: 'pointer' }} onClick={handleTitleComplete} />
                </>
              ) : (
                <>
                  {documentTitle}
                  <EditOutlinedIcon sx={{ marginLeft: '8px', verticalAlign: 'middle', cursor: 'pointer' }} onClick={handleTitleDoubleClick} />
                </>
              )}
            </Typography>
          </Box>*/}
        {/* 
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#FBE5C6',
            borderRadius: '8px',
            padding: '12px',
          }}>
            <Typography
              sx={{
                pr: '5px',
                color: "#FF9A56",
                textAlign: "left",
                fontFamily: "Poppins",
                fontSize: "16px",
                fontStyle: "normal",
                fontWeight: 500,
                lineHeight: "24px",
              }}
            >
              Save As
            </Typography>
            <KeyboardArrowDownOutlinedIcon htmlColor="#FF9A56" sx={{
              padding: '3px'
            }} />
          </Box> */}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            onClick={toggleLeftSidebar}
            sx={{
              position: "fixed",
              zIndex: 3000,
              top: "70px",
              left: LeftsidebarOpen ? "21.5%" : "0.5%",
              backgroundColor: "#FF9A56",
              borderRadius: LeftsidebarOpen ? "50%" : "12px",
              border: "2px solid #1a1a1a",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                transform: "scale(1.1)",
                backgroundColor: "#e8894d",
              },
              "&:active": {
                transform: "translate(1px, 1px)",
              },
            }}
          >
            {LeftsidebarOpen ? (
              <KeyboardTabOutlinedIcon
                sx={{
                  transform: "rotate(180deg)",
                  color: "#1a1a1a",
                  fontSize: "20px",
                }}
              />
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                 <KeyboardTabOutlinedIcon
                    sx={{
                    color: "#1a1a1a",
                    fontSize: "20px",
                    }}
                />
                <Typography sx={{ fontFamily: "Poppins", fontWeight: 700, fontSize: "14px", color: "#1a1a1a", pr: 1 }}>
                    Open Adjacency
                </Typography>
              </Box>
            )}
          </Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", flexDirection: "column" }}>
        <Box
          sx={{
            position: "fixed",
            top: "18.5px",
            display: "flex",
            right: sidebarOpen ? "25.5%" : "0.5%",
            alignItems: "center",
            backgroundColor: "#FF9A56",
            borderRadius: "10px",
            border: "2px solid #1a1a1a",
            padding: "8px 17px",
            cursor: "pointer",
            transition: "all 0.1s ease",
            "&:hover": {
              backgroundColor: "#e8894d",
            },
          }}
          onClick={() => setIsOpen(true)}
        >
          <Typography
            sx={{
              pl: "5px",
              color: "#FBE5C6",
              textAlign: "left",
              fontFamily: "Poppins",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: 500,
              lineHeight: "24px",
            }}
          >
            Save Graph
          </Typography>
        </Box>

        <GuidedTour
        steps={selectedTemplate === "housing" ? housingEditorTourSteps : inputGraphEditorTourSteps}
        tourId={selectedTemplate === "housing" ? "housing-editor" : "input-graph-editor"}
        autoStart={false}
        renderTrigger={(startTour) => (
            <Box
              sx={{
                position: "fixed",
                top: "18.5px",
                display: "flex",
                right: sidebarOpen ? "calc(25.5% + 140px)" : "calc(0.5% + 140px)",
                alignItems: "center",
                backgroundColor: "#FBE5C6",
                border: "2px solid #1a1a1a",
                borderRadius: "10px",
                padding: "8px 17px",
                cursor: "pointer",
                transition: "all 0.1s ease",
                "&:hover": {
                  backgroundColor: "#FF9A56",
                  "& .tour-btn-text": { color: "#1a1a1a" },
                  "& .tour-btn-icon": { color: "#1a1a1a" },
                },
              }}
              onClick={startTour}
            >
              <Typography
                className="tour-btn-icon"
                sx={{
                  fontSize: "16px",
                  lineHeight: 1,
                  mr: "6px",
                  color: "#FF9A56",
                  transition: "color 0.2s",
                }}
              >
                💡
              </Typography>
              <Typography
                className="tour-btn-text"
                sx={{
                  color: "#1a1a1a",
                  fontFamily: "Poppins",
                  fontSize: "16px",
                  fontWeight: 700,
                  lineHeight: "24px",
                  transition: "color 0.2s",
                }}
              >
                Tutorial
              </Typography>
            </Box>
          )}
        />
          <Box
            onClick={toggleSidebar}
            sx={{
              position: "fixed",
              zIndex: 3000,
              top: "70px",
              right: sidebarOpen ? "26%" : "0.5%",
              backgroundColor: "#FF9A56",
              borderRadius: sidebarOpen ? "50%" : "12px",
              border: "2px solid #1a1a1a",
              padding: "8px",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                transform: "scale(1.1)",
                backgroundColor: "#e8894d",
              },
              "&:active": {
                transform: "translate(1px, 1px)",
              },
            }}
          >
            {sidebarOpen ? (
              <KeyboardTabOutlinedIcon
                sx={{
                  color: "#1a1a1a",
                  fontSize: "20px",
                }}
              />
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                 <KeyboardTabOutlinedIcon
                    sx={{
                        transform: "rotate(180deg)",
                        color: "#1a1a1a",
                        fontSize: "20px",
                    }}
                />
                <Typography sx={{ fontFamily: "Poppins", fontWeight: 700, fontSize: "14px", color: "#1a1a1a", pl: 1 }}>
                    Open Sidebar
                </Typography>
              </Box>
            )}
          </Box>
      </Box>
      {/* </Box> */}
    </div>
  );
}